import React from "react";
import { Button } from "@/components/ui/button";
import { Check, ChevronRight } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";

const TECH_IMG = "/atlas-showcase.jpg";
const PHONE_IMG = "/atlas-phone.jpg";

export default function AtlasShowcase({ onRequestDemo }) {
  const { t, messages } = useI18n();
  const bullets = messages?.showcase?.bullets || [];

  return (
    <section
      id="producto"
      data-testid="atlas-showcase"
      className="py-16 lg:py-24 bg-slate-50/60 border-y border-slate-100"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-xl shadow-slate-300/40 ring-1 ring-slate-200 bg-slate-200 aspect-[4/5] sm:aspect-[5/6]">
              <img
                src={TECH_IMG}
                alt="Industrial technicians using ATLAS in the field"
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="showcase-tech-image"
              />
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(15,23,42,0.0) 40%, rgba(15,23,42,0.35) 100%)",
                }}
                aria-hidden
              />
            </div>

            <div
              data-testid="atlas-rugged-phone"
              className="hidden sm:block absolute -bottom-8 -right-4 md:-right-10 lg:-right-6 w-56 md:w-64 lg:w-72 drop-shadow-2xl"
            >
              <div className="relative rounded-[36px] overflow-hidden ring-2 ring-white/80 shadow-2xl bg-slate-900">
                <img
                  src={PHONE_IMG}
                  alt="ATLAS mobile app on a rugged industrial device"
                  className="w-full h-auto object-cover"
                  data-testid="showcase-phone-image"
                  loading="lazy"
                />
              </div>
            </div>
          </div>

          <div>
            <h2
              data-testid="showcase-title"
              className="font-display text-3xl sm:text-4xl lg:text-[44px] font-extrabold text-slate-900 leading-tight"
            >
              {t("showcase.titleA")}{" "}
              <span className="text-[#6B46C1]">{t("showcase.titleB")}</span>
            </h2>
            <p className="mt-4 text-slate-600 text-base sm:text-lg max-w-lg">
              {t("showcase.subtitle")}
            </p>

            <ul className="mt-8 space-y-4">
              {bullets.map((b, i) => (
                <li
                  key={i}
                  data-testid={`showcase-bullet-${i}`}
                  className="flex items-start gap-3 text-slate-800"
                >
                  <span className="mt-1 h-6 w-6 rounded-full bg-[#6B46C1]/10 flex items-center justify-center">
                    <Check className="text-[#6B46C1]" size={14} strokeWidth={3} />
                  </span>
                  <span className="text-base sm:text-lg">{formatBullet(b)}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10">
              <Button
                data-testid="showcase-request-demo-btn"
                onClick={onRequestDemo}
                className="bg-[#6B46C1] hover:bg-[#553C9A] text-white rounded-lg px-6 h-12 text-[15px] font-semibold shadow-md shadow-purple-300/50"
              >
                {t("cta.requestDemo")}
                <ChevronRight className="ml-1" size={18} />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function formatBullet(text) {
  const parts = text.split(" ");
  if (parts.length <= 1) return text;
  return (
    <>
      <span className="font-bold">{parts[0]}</span> {parts.slice(1).join(" ")}
    </>
  );
}
